import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { glossary, glossaryTerms, getTranslatedEntry } from "@/lib/glossary";
import { cn } from "@/lib/utils";

interface TermProps {
  /** Glossary key (e.g. "HPS"). Falls back to title/description props if not registered. */
  termKey?: string;
  title?: string;
  description?: string;
  children?: ReactNode;
  className?: string;
}

/**
 * Inline hoverable term. Renders its children with a dotted underline; on
 * hover/focus it reveals a small card with the full title and an explanation.
 * Looks up `termKey` in the central glossary, or accepts inline title/description.
 */
export const Term = ({ termKey, title, description, children, className }: TermProps) => {
  const { t } = useTranslation();
  const entry = termKey ? getTranslatedEntry(termKey) : undefined;
  const resolvedTitle = title ?? entry?.title;
  const resolvedDescription = description ?? entry?.description;

  if (!resolvedTitle || !resolvedDescription) {
    return <>{children ?? termKey}</>;
  }

  return (
    <HoverCard openDelay={120} closeDelay={80}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          className={cn(
            "underline decoration-dotted decoration-muted-foreground/60 underline-offset-[3px]",
            "cursor-help align-baseline transition-colors hover:decoration-primary hover:text-foreground",
            "focus-visible:outline-none focus-visible:decoration-primary focus-visible:text-foreground",
            className,
          )}
          aria-label={`${children ?? termKey}: ${resolvedTitle}`}
        >
          {children ?? termKey}
        </button>
      </HoverCardTrigger>
      <HoverCardContent className="w-72" align="start" sideOffset={6}>
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">
          {t("glossaryPage.title", { defaultValue: "Glossary" })}
        </p>
        <p className="mt-1 text-sm font-medium tracking-tight text-foreground">
          {resolvedTitle}
        </p>
        <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
          {resolvedDescription}
        </p>
        {termKey && entry && (
          <Link
            to={`/resources/glossary#${termKey.toLowerCase()}`}
            className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-primary hover:text-foreground"
          >
            {t("glossaryPage.viewInGlossary", { defaultValue: "View in glossary" })}{" "}
            <ArrowUpRight className="h-3 w-3" />
          </Link>
        )}
      </HoverCardContent>
    </HoverCard>
  );
};

interface GlossaryTextProps {
  children: string;
  className?: string;
}

/**
 * Wraps any string and automatically replaces registered glossary terms
 * with hoverable <Term> components. Whole-word, case-sensitive matching.
 *
 * Usage:  <GlossaryText>{t("hero.subtitle")}</GlossaryText>
 */
export const GlossaryText = ({ children, className }: GlossaryTextProps) => {
  if (!children || glossaryTerms.length === 0) {
    return <span className={className}>{children}</span>;
  }

  // Build a single regex that matches any registered term. We add a word
  // boundary only on sides that begin/end with a word character — unit
  // symbols like "μmol/J" or "mol/m²/day" don't satisfy \b on both ends.
  const alternation = glossaryTerms
    .map((term) => {
      const left = /^\w/.test(term) ? "\\b" : "";
      const right = /\w$/.test(term) ? "\\b" : "";
      return `${left}${escapeRegExp(term)}${right}`;
    })
    .join("|");
  const pattern = new RegExp(`(${alternation})`, "g");
  const parts = children.split(pattern);

  return (
    <span className={className}>
      {parts.map((part, i) =>
        glossary[part] ? <Term key={i} termKey={part}>{part}</Term> : <span key={i}>{part}</span>,
      )}
    </span>
  );
};

function escapeRegExp(str: string) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}